import {
  Box,
  Card,
  Heading,
  HStack,
  Image,
  Input,
  SimpleGrid,
  VStack,
} from "@chakra-ui/react";
import { ChangeEvent, Fragment, useCallback, useMemo, useState } from "react";
import { BiSearch } from "react-icons/bi";
import { useSearchParams } from "react-router";

import { AppModal } from "./AppModal";
import { AppModalSkeleton } from "./AppModalSkeleton";
import {
  APP_GRID_COLUMNS,
  filterablePropertiesForApps,
  FilterableProperty,
} from "./constants";

import { BuildOnPlumeButton } from "@/components/build-on-plume-button";
import { HubGuideButton } from "@/components/hub-guide-button";
import { PageLayout } from "@/components/page-layout";
import { RoundedBadge } from "@/components/rounded-badge";
import { RoundedButton } from "@/components/rounded-button";
import { InputGroup } from "@/components/ui/input-group";
import { useApps } from "@/hooks/useApps";
import { Category } from "@/types";

const categories: Category[] = [
  "All",
  "Bridge",
  "DEX",
  "Derivatives",
  "Earn",
  "Gaming",
  "Infra",
  "Launchpad",
  "Lending",
  "Marketplace",
];

export const Apps = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = (searchParams.get("category") as Category) || "All";
  const query = searchParams.get("query") || "";
  const [selectedCategory, setSelectedCategory] = useState<Category>(category);
  const [selectedFilterableProperty, setSelectedFilterableProperty] =
    useState<FilterableProperty>();
  const [searchQuery, setSearchQuery] = useState(query);
  const { apps, isLoadingApps } = useApps();

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value.toLowerCase());
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("query", e.target.value.toLowerCase());
      if (selectedCategory !== "All") {
        newSearchParams.set("category", selectedCategory);
      }
      setSearchParams(newSearchParams);
    },
    [setSearchQuery, selectedCategory, searchParams, setSearchParams],
  );

  const appsByQueryAndCategory = useMemo(() => {
    const sortedApps = [...apps].sort((a, b) => {
      if (a.isLive !== b.isLive) {
        return a.isLive ? -1 : 1;
      }
      return a.title.localeCompare(b.title);
    });
    const appsFilteredByQuery = sortedApps.filter(
      ({ title }) =>
        title.toLowerCase().includes(searchQuery) ||
        searchQuery.includes(title.toLowerCase()),
    );
    if (selectedCategory === "All") return appsFilteredByQuery;

    return appsFilteredByQuery.filter(
      ({ category }) =>
        selectedCategory.toLowerCase() === category.toLowerCase(),
    );
  }, [selectedCategory, apps, searchQuery]);

  const filteredApps = useMemo(() => {
    if (!selectedFilterableProperty) return appsByQueryAndCategory;
    return appsByQueryAndCategory.filter(selectedFilterableProperty.filterFn);
  }, [appsByQueryAndCategory, selectedFilterableProperty]);

  const topApps = useMemo(() => {
    return apps
      .filter((app) => app.isLive)
      .sort((a, b) => (b.ranking ?? 0) - (a.ranking ?? 0))
      .slice(0, 4);
  }, [apps]);

  return (
    <PageLayout
      title="Explore the Plume Ecosystem"
      tertiaryTitle="Discover RWAfi on Plume - stake, lend, borrow, loop, earn and more!"
      slotRight={
        <VStack justifyContent="space-evenly">
          <HStack>
            <BuildOnPlumeButton />
            <HubGuideButton />
          </HStack>
        </VStack>
      }
    >
      <VStack gap={4} alignItems="flex-start" w="full">
        <Heading fontSize="2xl">Featured</Heading>
        <SimpleGrid columns={[2, 2, 2, 4]} gap={4} w="full">
          {topApps.map((app) => (
            <AppModal
              app={app}
              key={`featured-${app.id}`}
              variant="small"
              modalId={`featured-${app.id}`}
            />
          ))}
          {isLoadingApps &&
            !topApps.length &&
            Array(4)
              .fill(0)
              .map((_, i) => <AppModalSkeleton key={i} variant="small" />)}
        </SimpleGrid>
      </VStack>
      <HStack gridColumnGap={0} maxW="100%" flexWrap="wrap">
        {categories.map((category) => (
          <Fragment key={category}>
            <RoundedButton
              onClick={() => {
                setSelectedCategory(category);
                const newSearchParams = new URLSearchParams(searchParams);
                if (category === "All") {
                  newSearchParams.delete("category");
                } else {
                  newSearchParams.set("category", category);
                }
                setSearchParams(newSearchParams);
              }}
              mx={1}
              px={6}
              variant={category === selectedCategory ? "solid" : "outline"}
              bgColor={category === selectedCategory ? "bg.inverted" : "bg"}
              color={category === selectedCategory ? "fg.inverted" : "fg.muted"}
              height={12}
              _hover={
                category !== selectedCategory
                  ? { bgColor: "bg.muted" }
                  : undefined
              }
            >
              {category}
            </RoundedButton>
          </Fragment>
        ))}
      </HStack>
      <VStack gap={4} alignItems="flex-start" w="full">
        <Heading fontSize="2xl" alignItems="center" display="flex" gap={2}>
          {selectedCategory} Apps{" "}
          {filteredApps?.length ? (
            <RoundedBadge>{filteredApps.length}</RoundedBadge>
          ) : (
            ""
          )}
        </Heading>
        <InputGroup
          startElement={<Box as={BiSearch} fontSize="xl" />}
          width="100%"
        >
          <Input
            borderRadius="3xl"
            placeholder="Search"
            w="100%"
            onChange={onChange}
            value={searchQuery}
            bgColor="white"
          />
        </InputGroup>
        <Box
          position="relative"
          w="full"
          h="40px"
          overflowX="auto"
          className="hide-scrollbar"
        >
          <HStack position="absolute" flexWrap="nowrap">
            {filterablePropertiesForApps(apps).map((p) => {
              const isSelected = p === selectedFilterableProperty;
              return (
                <RoundedButton
                  key={p.name}
                  onClick={() =>
                    isSelected
                      ? setSelectedFilterableProperty(undefined)
                      : setSelectedFilterableProperty(p)
                  }
                  variant={isSelected ? "solid" : "outline"}
                  bgColor={isSelected ? "bg.inverted" : "bg"}
                  color={isSelected ? "fg.inverted" : "fg.muted"}
                  py={1.5}
                  _hover={!isSelected ? { bgColor: "bg.muted" } : undefined}
                >
                  <Image src={p.icon} boxSize={6} />
                  {p.name}
                </RoundedButton>
              );
            })}
          </HStack>
        </Box>
      </VStack>
      {(isLoadingApps || !!filteredApps.length) && (
        <SimpleGrid columns={APP_GRID_COLUMNS} gap={6} w="full">
          {filteredApps.map((app) => (
            <AppModal
              app={app}
              key={`grid-${app.id}`}
              modalId={`grid-${app.id}`}
            />
          ))}

          {isLoadingApps &&
            !filteredApps?.length &&
            Array(9)
              .fill(0)
              .map((_, i) => <AppModalSkeleton key={i} />)}
        </SimpleGrid>
      )}
      {!isLoadingApps && !filteredApps?.length && (
        <Card.Root
          rounded="16px"
          p={6}
          bg="bg"
          w="full"
          py={{ base: "98px", md: "110px" }}
        >
          <Card.Body>
            <VStack gap={4} justifyContent="center" alignItems="center">
              <Box p={3} bg="#E7E7E7" rounded="14px">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M21.7953 20.2044L17.3431 15.7504C18.678 14.0108 19.3013 11.8286 19.0864 9.64643C18.8715 7.46426 17.8347 5.44552 16.1861 3.99972C14.5376 2.55393 12.4008 1.78935 10.2092 1.86107C8.01768 1.9328 5.93546 2.83546 4.38497 4.38595C2.83448 5.93644 1.93182 8.01865 1.86009 10.2102C1.78837 12.4018 2.55295 14.5385 3.99875 16.1871C5.44454 17.8356 7.46328 18.8725 9.64545 19.0874C11.8276 19.3023 14.0098 18.679 15.7494 17.3441L20.2053 21.801C20.31 21.9056 20.4342 21.9886 20.5709 22.0453C20.7077 22.1019 20.8542 22.1311 21.0022 22.1311C21.1502 22.1311 21.2967 22.1019 21.4335 22.0453C21.5702 21.9886 21.6944 21.9056 21.7991 21.801C21.9037 21.6963 21.9867 21.5721 22.0434 21.4354C22.1 21.2986 22.1291 21.1521 22.1291 21.0041C22.1291 20.8561 22.1 20.7096 22.0434 20.5728C21.9867 20.4361 21.9037 20.3119 21.7991 20.2072L21.7953 20.2044ZM4.12438 10.5004C4.12438 9.2395 4.49827 8.00696 5.19876 6.9586C5.89926 5.91024 6.8949 5.09314 8.05978 4.61063C9.22466 4.12812 10.5065 4.00187 11.7431 4.24785C12.9797 4.49383 14.1156 5.10099 15.0072 5.99255C15.8987 6.88411 16.5059 8.02003 16.7519 9.25666C16.9979 10.4933 16.8716 11.7751 16.3891 12.94C15.9066 14.1048 15.0895 15.1005 14.0411 15.801C12.9928 16.5015 11.7602 16.8754 10.4994 16.8754C8.80916 16.8736 7.18866 16.2014 5.9935 15.0062C4.79833 13.8111 4.12612 12.1906 4.12438 10.5004Z"
                    fill="#989898"
                  />
                </svg>
              </Box>
              <VStack gap={2} textAlign="center">
                <Heading fontSize="2xl" fontWeight="700" lineHeight="32px">
                  No results found
                </Heading>
                <Heading fontSize="lg" fontWeight="500" color="text.secondary">
                  Try adjusting the filters or search for something else
                </Heading>
              </VStack>
            </VStack>
          </Card.Body>
        </Card.Root>
      )}
    </PageLayout>
  );
};
